import { ChangeEvent } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { IProductModel } from 'types';

import ProductFieldInput from 'components/common/ProductFieldInput';
import ParameterCheckbox from './fields/ParameterCheckbox';
import ParameterDropdown from './fields/ParameterDropdown';
import ParameterInputList from './fields/ParameterInputList';
import ParameterButton from './fields/ParameterButton';
import ParameterExpressions from './fields/ParameterExpressions';
import EditMultiInput from './EditMultiInput';

export type ParameterValueProps = {
  itemModel: IProductModel;
  item: string;
  modelKey: string;
  parameter: Record<string, string | boolean>;
  addListItem: (key: string | number) => void;
  deleteListItem: (paramKey: string | number, key: string | number, exKey: string | number) => void;
  onListItemChange: (
    key: string | number,
    exKey: string,
    data: { [x: string]: string },
    addNew: boolean | undefined
  ) => void;
  onDropDownChange: (name: string, value: string) => void;
  updateSaveParameters: (name: string | number, value: string | boolean) => void;
  onCheckboxClicked: (e: ChangeEvent<HTMLInputElement>) => void;
  onChangeText: (e: { target: { name: string; value: string } }) => void;
  addCondition: (key: string | number) => void;
  onExpressionChange: (
    key: string | number,
    exKey: string | number,
    data: Record<string, string>,
    addNew: boolean | undefined
  ) => void;
  deleteExpression: (paramKey: string | number, key: string | number, exKey: string | number) => void;
  paramKey: string | number;
};

const ParameterValue = ({
  itemModel,
  item,
  modelKey,
  parameter,
  addListItem,
  deleteListItem,
  onListItemChange,
  onDropDownChange,
  updateSaveParameters,
  onCheckboxClicked,
  onChangeText,
  addCondition,
  onExpressionChange,
  deleteExpression,
  paramKey,
}: ParameterValueProps) => {
  if (!itemModel || !modelKey) return null;

  const getCheckboxValue = (val: boolean | string) => {
    if (val === undefined && modelKey === 'isPreloaded') {
      updateSaveParameters(modelKey, true);
      return true;
    }
    if (typeof val === 'string') {
      return val === 'true';
    }

    return val;
  };

  if (modelKey === 'parameterType') {
    return (
      <Flex flex={5} flexDir="row" justifyContent="flex-end">
        <Box textTransform="uppercase" pr={2.5}>
          {item}
        </Box>
      </Flex>
    );
  }

  if (itemModel.inputType === 'boolean') {
    return (
      <ParameterCheckbox name={modelKey} value={getCheckboxValue(parameter[modelKey])} onChange={onCheckboxClicked} />
    );
  }

  if (itemModel.inputType === 'select') {
    return (
      <ParameterDropdown
        selectValue={modelKey}
        value={parameter[modelKey] as string}
        filterData={itemModel.values}
        setFilteredData={onDropDownChange}
      />
    );
  }

  if (itemModel.inputType === 'textList') {
    const renderInputList =
      parameter.type === 'boolean' ? (
        <ParameterInputList
          parameter={parameter}
          modelKey={modelKey}
          paramKey={paramKey}
          deleteListItem={deleteListItem}
          onListItemChange={onListItemChange}
        />
      ) : (
        <>
          <ParameterInputList
            parameter={parameter}
            modelKey={modelKey}
            paramKey={paramKey}
            deleteListItem={deleteListItem}
            onListItemChange={onListItemChange}
          />
          <ParameterButton modelKey={modelKey} onClick={addListItem} title="Add value" />
        </>
      );

    return <EditMultiInput>{renderInputList}</EditMultiInput>;
  }

  if (itemModel.inputType === 'expression') {
    return (
      <EditMultiInput w="full" justifyContent="flex-end">
        <ParameterExpressions
          modelKey={modelKey}
          paramKey={paramKey}
          parameter={parameter}
          onExpressionChange={onExpressionChange}
          deleteExpression={deleteExpression}
        />
        <ParameterButton modelKey={modelKey} onClick={addCondition} title="Add condition" />
      </EditMultiInput>
    );
  }

  return (
    <Flex flex={5} flexDir="row" justifyContent="flex-end">
      <ProductFieldInput name={modelKey} value={parameter[modelKey] as string} onChange={onChangeText} />
    </Flex>
  );
};

export default ParameterValue;
